import {
  LockIcon,
  ShoppingCartAdd02Icon,
  ShoppingCartRemove01Icon
} from '@hugeicons/react'
import { AppIcon } from '@/shared/ui/icons'
import { clsx } from 'clsx'
import _ from 'lodash'

import { useBlocker } from '@/shared/hooks/useBlocker'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { Card } from '@/shared/ui'
import { Badge } from '@/shared/ui/Badge'
import { Button } from '@/shared/ui/Button'
import {
  FeaturedListCardStrip,
  featuredListCardClassName
} from '@/shared/ui/FeaturedListCardStrip'
import { GameIcon } from '@/shared/ui/GameIcon'
import { useResourceContext } from '@/shared/ui/Resource'
import { Resource } from '@/shared/ui/Resource'

import { useDigitalProductStore } from './hook'
import { BOOTH_PICKUP_LABEL, isBoothPickup } from './lib/boothPickup'
import {
  CHECKIN_REQUIRED_REASON,
  showCheckinRequiredAlert
} from './lib/checkinRequiredNotice'
import { isRedeemBlocked, itemPrice, redeemBlockMessage } from './lib/redeemBlock'
import { StorePartnerBadge } from './components/StorePartnerBadge'
import { resolveBlockLabel } from './lib/resolveBlockLabel'
import { isSoldOut } from './lib/stock'
import { showStoreClosedAlert } from './lib/storeClosedNotice'
import { canRedeemFromStore } from './lib/storeHours'

export const DigitalProductStoreCard = ({
  name,
  image,
  reward_category,
  reward_items,
  max_quantity,
  quantity,
  quantity_sold,
  position,
  coin,
  price,
  delivery_mode,
  redeemable,
  redeem_blocked_reason,
  sold_out,
  reward,
  rewardRequestsCount,
  partner,
  checkinPartner,
  onShow
}) => {
  const soldOut = isSoldOut({ quantity, quantity_sold })

  const { checkBlockCondition, showBlockAlert } = useBlocker()
  const { navigateTo } = useNavigation()
  const blockResult = checkBlockCondition(reward._blockCondition)

  /* A vitrine continua navegável fora da janela — quem chega cedo vê o que vai
     poder trocar. O que não acontece é abrir a folha de resgate: escolher
     variação e quantidade para receber `reward_shop_closed` no envio é
     descobrir tarde demais. */
  const { storeStatus, openingHours } = useDigitalProductStore()
  const isStoreClosed = !canRedeemFromStore(storeStatus)
  /* Esgotado vence bloqueado: mesmo liberado, o item esgotado não estaria
     disponível, então o rótulo que importa é o que encerra a possibilidade e
     não o que a condiciona. Os dois juntos dariam duas respostas à mesma
     pergunta. */
  const blockLabel =
    !blockResult.unlocked && !soldOut
      ? resolveBlockLabel(blockResult.firstUnmet)
      : null

  // A API decide o que está liberado; aqui só se mostra o porquê. Item barrado
  // continua na vitrine — fechado não é o mesmo que escondido.
  const redeemBlocked = isRedeemBlocked({ redeemable })

  const displayPrice = itemPrice({ price, coin })

  // Retirada no estande do parceiro, não no ponto central — o participante
  // precisa saber antes de abrir o item, senão descobre na hora de resgatar.
  const boothPickup = isBoothPickup({ delivery_mode })

  const handleShow = () => {
    if (!blockResult.unlocked) {
      return showBlockAlert(blockResult.firstUnmet)
    }

    /* Depois do bloqueio do item: um item bloqueado numa loja fechada tem dois
       motivos, e o que o Participante destrava é o do item. */
    if (isStoreClosed) {
      return void showStoreClosedAlert(openingHours?.openAt)
    }

    // Check-in é o único motivo de `redeem_blocked_reason` com destino — os
    // outros (esgotado, fora de curadoria, limite atingido) não têm ação, só
    // o texto inerte no rodapé da folha. Este intercepta o toque e leva até o
    // parceiro em vez de abrir a folha para mostrar um botão travado.
    if (redeemBlocked && redeem_blocked_reason === CHECKIN_REQUIRED_REASON) {
      return void showCheckinRequiredAlert(checkinPartner, navigateTo)
    }

    // Abrir para ver os detalhes continua valendo com o resgate barrado: o
    // participante precisa saber o que é o item para decidir voltar.
    return onShow()
  }

  return (
    <Card
      flush={boothPickup}
      className={clsx('h-full shadow', {
        'cursor-pointer hover:shadow-lg': blockResult.unlocked,
        [featuredListCardClassName('booth')]: boothPickup
      })}
    >
      {boothPickup && (
        <FeaturedListCardStrip label={BOOTH_PICKUP_LABEL} tone="booth" />
      )}
      <div className="text-center group relative" onClick={handleShow}>
        {/* `opacity-65 grayscale` é o tratamento que o app já dá a thumb de
            recurso bloqueado (`JourneyListCard`). Sem ele o cadeado disputava
            legibilidade com a foto do brinde em cor cheia, e o item bloqueado
            lia como disponível. */}
        <div
          className={clsx(
            'bg-no-repeat bg-center bg-cover rounded-t-lg h-[200px] bg-brand',
            !blockResult.unlocked && 'opacity-65 grayscale'
          )}
          style={{
            backgroundImage: `url('${_.get(
              image,
              'file.url',
              '/placeholders/reward.jpg'
            )}')`
          }}
        />

        {partner?.label ? (
          <div className="absolute inset-x-2 top-2 z-[2] flex justify-end">
            <StorePartnerBadge label={partner.label} overlay />
          </div>
        ) : null}

        <div
          className={`absolute inset-0 flex items-center justify-center bg-black/25 rounded-t-lg transition-opacity ${
            !blockResult.unlocked
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          {/* `size` explícito: `AppIcon` resolve pixels pelos tokens legados
              `fs-*`, e `text-5xl` não está nesse mapa — caía no default de
              20px, então o cadeado nunca teve o tamanho que o código pedia. */}
          <AppIcon
            name={!blockResult.unlocked ? 'lock' : 'plus'}
            size={52}
            className="text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.45)]"
          />
        </div>
        <div className="absolute bottom-0 right-0 mr-3 mb-3 z-[1]">
          {soldOut ? (
            <Badge variant="locked">Esgotado!</Badge>
          ) : (
            <Button
              variant="primary"
              icon
              size="sm"
              onClick={handleShow}
              leadingIcon={<ShoppingCartAdd02Icon />}
              aria-label="Reservar"
            />
          )}
        </div>
        {soldOut && (
          <div className="absolute bg-white opacity-25 rounded-t-lg w-full h-full top-0 left-0"></div>
        )}
      </div>
      <Card.Body className="p-3 flex flex-col" onClick={handleShow}>
        {reward_category?.name ? (
          <span className="text-brand font-bold text-caps uppercase block mb-1">
            {reward_category.name}
          </span>
        ) : null}
        <div className="mb-3">
          <span className="text-gray-800 cursor-pointer font-bold hover:text-brand text-base block">
            {name}
          </span>
          {/* Linha de texto, e não `Badge`: o pill do design system é de
              altura fixa, `white-space: nowrap` e caixa alta, e a duas colunas
              a 375px sobram ~119px — cerca de 18 caracteres. "Esgotado!" cabe
              ali porque tem nove; um rótulo que nomeia missão ou Parceiro não
              cabe em nenhum caso realista, e sem `overflow` declarado ele vaza
              por cima do card. O cadeado e o cinza mantêm a família visual sem
              herdar a restrição de altura. Ver `findings.md` do artefato.

              Âmbar e não o cinza do variant `locked`: este rótulo carrega
              informação nova e acionável — o que fazer para liberar o item — e
              não o estado redundante que a imagem escurecida e o cadeado já
              dizem. `warning-text` mede 5.05:1 sobre branco, contra os 2.45:1
              do par cinza do pill, e não colide com o roxo da categoria nem do
              preço no mesmo card. */}
          {blockLabel ? (
            <span className="mt-1 flex items-start gap-1 text-2xs font-semibold leading-snug text-warning-text">
              <LockIcon
                size={12}
                variant="duotone"
                className="mt-px shrink-0"
                aria-hidden
              />
              {/* `min-w-0 flex-1`: sem reivindicar a largura restante o span
                  fica com largura de conteúdo encolhida, quebra cedo e o clamp
                  corta o que sobra — "Falta: Nível 2" virava "Falta: / Nível".
                  Três linhas em vez de duas dão folga para o nome inteiro de
                  missão e Parceiro, que é o que o rótulo existe para mostrar. */}
              <span className="line-clamp-3 min-w-0 flex-1 break-words">
                {blockLabel}
              </span>
            </span>
          ) : null}
          {rewardRequestsCount > 0 &&
            (rewardRequestsCount == max_quantity ? (
              <Badge variant="locked">Já reservou o máximo</Badge>
            ) : (
              <Badge variant="progress">
                Reservou {rewardRequestsCount} de até {max_quantity}
              </Badge>
            ))}
        </div>
        <div className="mt-auto flex items-center justify-between">
          {!soldOut && (
            <div className="font-semibold text-sm text-brand flex items-center hover:text-brand">
              <GameIcon name="coins" size={24} className="mr-2" />
              <span className="block font-extrabold text-2xl leading-none">
                {displayPrice || 'Grátis'}
              </span>
            </div>
          )}
        </div>
        {redeemBlocked && (
          <p className="m-0 mt-3 text-2xs font-semibold leading-snug text-gray-600">
            {redeemBlockMessage(redeem_blocked_reason)}
          </p>
        )}
      </Card.Body>
    </Card>
  )
}
